/**
 * Static site search index.
 *
 * Powers /search, which is the target of the schema.org WebSite SearchAction.
 * Google only honors a sitelinks searchbox when the declared target is a REAL,
 * working search URL — so any route added here must actually exist.
 *
 * Keep this to indexable, public pages only (no admin, QA, auth, or checkout).
 */
import { BLOG_POSTS } from "@/content/blog";

export type SearchEntry = {
  path: string;
  title: string;
  description: string;
  /** Extra match terms not present in the title/description. */
  keywords: string[];
  group: "Page" | "Offer" | "Article" | "Resource";
};

const PAGES: SearchEntry[] = [
  { path: "/", title: "Dr. Sully — Home", description: "Industrial-organizational psychology, executive consulting, and speaking with Dr. RD 'Sully' Sullivan.", keywords: ["home", "dr sully", "makechange"], group: "Page" },
  { path: "/about", title: "About Dr. Sully", description: "Credentials, story, and the work behind PsyD · ThD · DMin · CERC.", keywords: ["bio", "credentials", "story"], group: "Page" },
  { path: "/framework", title: "The MAKECHANGE Framework", description: "The 10 pillars: Momentum, Authority, Kingdom-Focus, Equity, Clarity, Healing, Action, Navigation, Growth, Empowerment.", keywords: ["framework", "pillars", "operating system", "makechange"], group: "Page" },
  { path: "/scan", title: "Free Make Change Scan", description: "Two minutes to pinpoint the bottleneck holding your next level back.", keywords: ["free", "assessment", "quiz", "diagnostic"], group: "Offer" },
  { path: "/masterclass", title: "Built On Purpose Masterclass", description: "Live masterclass on turning calling into a working operating system.", keywords: ["masterclass", "live", "training", "register"], group: "Offer" },
  { path: "/invest", title: "Invest — Programs & Pricing", description: "Coaching, consulting, and program pricing.", keywords: ["pricing", "cost", "packages", "invest"], group: "Offer" },
  { path: "/book", title: "Book a Session", description: "Choose a session, coaching block, or membership and check out securely.", keywords: ["booking", "schedule", "session", "checkout"], group: "Offer" },
  { path: "/inquire", title: "Work With Dr. Sully", description: "Start a conversation about coaching, consulting, or advocacy.", keywords: ["contact", "hire", "inquiry"], group: "Page" },
  { path: "/strategy-session", title: "Strategy Session Application", description: "Apply for a private strategy session to map your next move.", keywords: ["apply", "consult", "strategy"], group: "Offer" },
  { path: "/speaking", title: "Speaking & Keynotes", description: "Keynotes, workshops, and trainings for corporate, government, education, and faith audiences.", keywords: ["keynote", "speaker", "conference", "event", "workshop"], group: "Page" },
  { path: "/books", title: "Books by Dr. Sully", description: "Print titles on leadership, healing, and building on purpose.", keywords: ["books", "author", "print", "paperback"], group: "Resource" },
  { path: "/ebooks", title: "Resources & eBooks", description: "Downloadable guides, workbooks, and playbooks.", keywords: ["ebook", "pdf", "download", "workbook", "guide", "resources"], group: "Resource" },
  { path: "/blog", title: "Blog", description: "Essays on healing, leadership, and building on purpose.", keywords: ["articles", "essays", "writing"], group: "Page" },
  { path: "/advocacy", title: "Full-Scope Advocacy Partner", description: "Faith-integrated, trauma-informed advocacy partnership based in San Antonio with travel availability.", keywords: ["advocacy", "fsap", "san antonio", "support"], group: "Offer" },
  { path: "/advocacy/pricing", title: "Advocacy Pricing", description: "Retainer tiers, travel policy, and compassion pricing.", keywords: ["retainer", "advocacy cost", "travel"], group: "Offer" },
  { path: "/advocacy/how-it-works", title: "How Advocacy Works", description: "The intake, planning, and partnership process step by step.", keywords: ["process", "intake", "steps"], group: "Page" },
  { path: "/reviews", title: "Reviews", description: "What clients and audiences say about working with Dr. Sully.", keywords: ["testimonials", "reviews", "proof"], group: "Page" },
  { path: "/stories", title: "Stories", description: "Client and community stories of change.", keywords: ["stories", "impact"], group: "Page" },
  { path: "/free", title: "Free Resources", description: "Free tools and entry points into the MAKECHANGE work.", keywords: ["free", "tools"], group: "Resource" },
  { path: "/legal", title: "Legal & Disclaimers", description: "Terms, privacy, and ethical disclaimers.", keywords: ["terms", "privacy", "policy", "disclaimer"], group: "Page" },
];

const ARTICLES: SearchEntry[] = BLOG_POSTS.map((post) => ({
  path: `/blog/${post.slug}`,
  title: post.title,
  description: post.description,
  keywords: [...post.keywords, post.category.toLowerCase()],
  group: "Article" as const,
}));

export const SEARCH_INDEX: SearchEntry[] = [...PAGES, ...ARTICLES];

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9\s]/g, " ");

/** Simple weighted term match — title hits rank above body/keyword hits. */
export function searchSite(query: string, limit = 20): SearchEntry[] {
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return [];

  return SEARCH_INDEX.map((entry) => {
    const title = normalize(entry.title);
    const description = normalize(entry.description);
    const keywords = normalize(entry.keywords.join(" "));
    let score = 0;

    for (const term of terms) {
      if (title.includes(term)) score += 6;
      if (keywords.includes(term)) score += 3;
      if (description.includes(term)) score += 2;
      if (normalize(entry.path).includes(term)) score += 1;
    }
    // Require at least one term to land somewhere.
    const matchedAll = terms.every(
      (t) =>
        title.includes(t) || keywords.includes(t) || description.includes(t) || entry.path.includes(t),
    );
    return { entry, score: matchedAll ? score + 2 : score };
  })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title))
    .slice(0, limit)
    .map((r) => r.entry);
}
